import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import React, { useEffect, useState, useRef } from "react";
import DashboardIcon from '@material-ui/icons/Dashboard';
import AddAlert from "@material-ui/icons/AddAlert";
import { ref, onValue } from "firebase/database";
import { useLocation } from 'react-router-dom';
import { useSelector } from "react-redux";
import html2canvas from "html2canvas";
import moment from "moment-timezone";
import { jsPDF } from "jspdf";

import { API_BASE_URL, CSV_DATE_FORMAT, GRAPH_DATA_REQUEST, UTC_TIME_ZONE } from "../../common/constants";
import { isFdaViewEnabled, downloadBlobFile, arrayToCsvFormat } from "common/helpers";
import { SUCCESS_NOTIFICATION_REQUEST } from "../../redux/notifications/actions";
import { firebaseMessaging, firebaseDB, setupDB } from "../../firebase";
import { FETCH_USER_LIST_REQUEST } from "../../redux/users/actions";
import { FETCH_GUESTS_REQUEST } from "../../redux/guestMap/actions";
import GridContainer from "components/Grid/GridContainer.js";
import { useComposeDispatcher } from "../../common/hooks";
import NewFiltersBox from 'views/FilterBox/NewFiltersBox';
import Button from "components/CustomButtons/Button.js";
import Snackbar from "components/Snackbar/Snackbar.js";
import Loader from "components/Loader/Loader.js";
import { fetchRequest } from "common/service";
import Configuration from "./Configuration";
import GraphDetail from "./GraphDetail.js";
import DevicesNew from "./DevicesNew.js";
import Pagination from "./Pagination";
import {
  UPDATE_SENSOR_CONTROL_TABLE_REQUEST,
  FETCH_NEW_USER_MIN_DATA_REQUEST,
  FETCH_DEVICE_DATA_VIEW_REQUEST,
  UPDATE_SENSOR_CONTROL_REQUEST,
  FETCH_DEVICE_DETAILS_REQUEST,
  FETCH_SENSOR_CONTROL_REQUEST,
  FETCH_NEW_USER_DATA_REQUEST,
  FETCH_FRIENDLY_NAME_REQUEST,
  FETCH_MIN_CSV_DATA_REQUEST,
  FETCH_NEW_DEV_DATA_REQUEST,
  FETCH_SVITTAL_DATA_REQUEST,
  FETCH_NEW_SV_DATA_REQUEST,
  FETCH_DEVICE_DATA_REQUEST,
  FETCH_PARAMS_DATA_REQUEST,
  SAVE_USER_ORDER_REQUEST,
  FETCH_CSV_DATA_REQUEST,
  FETCH_NEW_DATA_REQUEST,
  FETCH_DEVICE_REQUEST,
  RESET_GRAPH_REQUEST,
  START_GRAPH_LOADER
} from "../../redux/device/actions";

const ViewModeToggle = ({ viewMode, setViewMode, showToggle }) => {
  // Don't render if toggle should not be shown
  if (!showToggle) {
    return null;
  }
  const toggleButtonStyle = {
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 16px',
    cursor: 'pointer',
    fontWeight: 'normal',
    transition: 'all 0.3s ease',
    fontSize: '13px',
    gap: '6px',
    minWidth: '120px',
    position: 'relative'
  };

  const activeButtonStyle = {
    ...toggleButtonStyle,
    backgroundColor: '#f44336',
    color: 'white',
    fontWeight: 'bold'
  };

  const inactiveButtonStyle = {
    ...toggleButtonStyle,
    backgroundColor: 'white',
    color: 'black'
  };

  const indicatorStyle = {
    position: 'absolute',
    bottom: '0',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '20px',
    height: '3px',
    backgroundColor: '#fff',
    borderRadius: '2px'
  };

  const containerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };

  const toggleContainerStyle = {
    display: 'flex',
    border: '1px solid #ccc',
    borderRadius: '4px',
    overflow: 'hidden',
    width: 'fit-content',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  };

  return (
    <div style={containerStyle}>
      <div style={toggleContainerStyle}>
        <div
          onClick={() => setViewMode('default')}
          style={viewMode === 'default' ? activeButtonStyle : inactiveButtonStyle}
          onMouseEnter={(e) => {
            if (viewMode !== 'default') {
              e.target.style.backgroundColor = '#f5f5f5';
            }
          }}
          onMouseLeave={(e) => {
            if (viewMode !== 'default') {
              e.target.style.backgroundColor = 'white';
            }
          }}
        >
          <DashboardIcon style={{ fontSize: '16px' }} />
          Default View
          {viewMode === 'default' && <div style={indicatorStyle} />}
        </div>
        <div
          onClick={() => setViewMode('hrrr')}
          style={viewMode === 'hrrr' ? activeButtonStyle : inactiveButtonStyle}
          onMouseEnter={(e) => {
            if (viewMode !== 'hrrr') {
              e.target.style.backgroundColor = '#f5f5f5';
            }
          }}
          onMouseLeave={(e) => {
            if (viewMode !== 'hrrr') {
              e.target.style.backgroundColor = 'white';
            }
          }}
        >
          <LocalHospitalIcon style={{ fontSize: '16px' }} />
          HR/RR View
          {viewMode === 'hrrr' && <div style={indicatorStyle} />}
        </div>
      </div>
    </div>
  );
};

export default function MonitorDashboard() {
  const location = useLocation();
  const isConfigurationPage = location.pathname === '/admin/configuration';

  const searchParams = new URLSearchParams(location.search);
  const showToggle = searchParams.get('toggleview') === '1';
  const isFdaMode = isFdaViewEnabled();

  const itemsPerPage = 20;
  const userDetails = JSON.parse(localStorage.getItem("user-details"));
  const loggedUserId = userDetails !== null ? userDetails.userId : "";
  const loggedUserRole = userDetails !== null ? userDetails.roles[0].role : "";
  const loggedUserSupplier = userDetails !== null ? userDetails.roles[0].supplier : "";

  const svittalDashboardDrawerRef = useRef(null);
  const devTimerToClearSomewhere = useRef(null);
  const timerToClearSomewhere = useRef(null);
  const svittalDrawerRef = useRef(null);
  const drawerRef = useRef(null);

  const { device, guestMapData } = useSelector((state) => ({
    guestMapData: state.guestMap,
    device: state.device
  }));

  const [userInfo, setUserInfo] = useState({
    deviceDataModelId: "d36a4373-fbdc-44a3-8c96-4bb920041e40",
    supplierId: "cd5dc314-6e25-48a8-9d47-9e574b8fe3a0",
    intervalStartTime: new Date(),
    friendlyName: "all",
    interval: "2 Hr"
  });

  const [pVitalInterval, setPVitalInterval] = useState(7500000);  //125 min default
  const [isSensorPositionSaved, setIsSensorPositionSaved] = useState(false);
  const [viewMode, setViewMode] = useState(isFdaMode ? 'hrrr' : 'default');
  const [selectedStartDate, handleStartDateChange] = useState(new Date());
  const [selectedEndDate, handleEndDateChange] = useState(new Date());
  const [fetchGraphDataCall, setFetchGraphDataCall] = useState(false);
  const [firebaseInitilize, setFirebaseInitilize] = useState(false);
  const [showSensorControl, setShowSensorControl] = useState(false);
  const [csvDownloadReady, setCsvDownloadReady] = useState(false);
  const [userTimeZone, setUserTimeZone] = useState(UTC_TIME_ZONE);
  const [firstPVitalHit, setFirstPVitalHit] = useState(true);
  const [disablePdfBtn, setDisablePdfBtn] = useState(false);
  const [sensorsOrderData, setSensorsOrderData] = useState({});
  const [apiRequestCount, setApiRequestCount] = useState(0);
  const [firebaseDBData, setFirebaseDBData] = useState({});
  const [dataCenterData, setDataCenterData] = useState({});
  const [dayLightSaving, setDayLightSaving] = useState(0);
  const [startTime, setStartTime] = useState(Date.now());
  const [userWithOrder, setUserWithOrder] = useState({});
  const [userMacAddress, setUserMacAddress] = useState();
  const [thresholdData, setThresholdData] = useState([]);
  const [toastStatus, setToastStatus] = useState(false);
  const [showSensors, setShowSensors] = useState(false);
  const [autoFetchData, setAutoFetchData] = useState(1);
  const [alertMessage, setAlertMessage] = useState("");
  const [guestSensors, setGuestSensors] = useState([]);
  const [endTime, setEndTime] = useState(Date.now());
  const [friendlyName, setFriendlyName] = useState();
  const [companyList, setCompanyList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [paramsData, setParamsData] = useState([]);
  const [lastFilter, setLastFilter] = useState({});
  const [nextCall, setNextCall] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [devDataId, setDevDataId] = useState();
  const [drawer, setDrawer] = useState(false);
  const [sensor, setSensor] = useState({});
  const [offset, setOffset] = useState(0);
  const [abbr, setAbbr] = useState("GMT");

  const [updateSensorControlTableRequest] = useComposeDispatcher(UPDATE_SENSOR_CONTROL_TABLE_REQUEST);
  const [updateSensorControlRequest] = useComposeDispatcher(UPDATE_SENSOR_CONTROL_REQUEST);
  const [successNotificationRequest] = useComposeDispatcher(SUCCESS_NOTIFICATION_REQUEST);
  const [fetchUserMinDeviceData] = useComposeDispatcher(FETCH_NEW_USER_MIN_DATA_REQUEST);
  const [getDeviceDataViewList] = useComposeDispatcher(FETCH_DEVICE_DATA_VIEW_REQUEST);
  const [fetchFriendlyNameData] = useComposeDispatcher(FETCH_FRIENDLY_NAME_REQUEST);
  const [sensorControlRequest] = useComposeDispatcher(FETCH_SENSOR_CONTROL_REQUEST);
  const [fetchUserDeviceData] = useComposeDispatcher(FETCH_NEW_USER_DATA_REQUEST);
  const [getDeviceDataById] = useComposeDispatcher(FETCH_DEVICE_DETAILS_REQUEST);
  const [svittalDataRequest] = useComposeDispatcher(FETCH_SVITTAL_DATA_REQUEST);
  const [newDevDataRequest] = useComposeDispatcher(FETCH_NEW_DEV_DATA_REQUEST);
  const [paramsDataRequest] = useComposeDispatcher(FETCH_PARAMS_DATA_REQUEST);
  const [getDeviceDataList] = useComposeDispatcher(FETCH_DEVICE_DATA_REQUEST);
  const [saveUserDeviceOrder] = useComposeDispatcher(SAVE_USER_ORDER_REQUEST);
  const [newSvDataRequest] = useComposeDispatcher(FETCH_NEW_SV_DATA_REQUEST);
  const [fetchMinCsvData] = useComposeDispatcher(FETCH_MIN_CSV_DATA_REQUEST);
  const [newDataRequest] = useComposeDispatcher(FETCH_NEW_DATA_REQUEST);
  const [startGraphLoader] = useComposeDispatcher(START_GRAPH_LOADER);
  const [fetchCsvData] = useComposeDispatcher(FETCH_CSV_DATA_REQUEST);
  const [getUserList] = useComposeDispatcher(FETCH_USER_LIST_REQUEST);
  const [getGuestsList] = useComposeDispatcher(FETCH_GUESTS_REQUEST);
  const [getDeviceList] = useComposeDispatcher(FETCH_DEVICE_REQUEST);
  const [resetGraphData] = useComposeDispatcher(RESET_GRAPH_REQUEST);

  useEffect(() => {
    setShowSensors(false);
  }, [location.pathname]);

  useEffect(() => {
    getDeviceDataById('6df2d80f-be28-47ac-a509-8f0b6aa3bbb8');
    getDeviceList({ status: "Active" });

    if (loggedUserRole === "Patient") {
      setUserInfo({ ...userInfo, userId: loggedUserId });
      fetchFriendlyNameData({ userId: loggedUserId, deviceDataModelId: userInfo.deviceDataModelId });
    }

    if (loggedUserRole !== "TenantAdmin" && loggedUserRole !== "Patient") {
      getUserList({ role: "Patient", supplierId: loggedUserSupplier?.supplierId });
    }
    if (loggedUserRole === "Guest") {
      getGuestsList({ "deviceDataModelId": "299992ad-6d4a-4020-8501-8a004d45fffc" });
      fetchFriendlyNameData({ deviceDataModelId: userInfo.deviceDataModelId });
    }

    if(!firebaseInitilize) {
      setupDB();
      setFirebaseInitilize(true);
      const dbRef = ref(firebaseDB, "staticdata");
  
      const unsubscribe = onValue(dbRef, (snapshot) => {
        const fetchedData = snapshot.val();
        setFirebaseDBData(fetchedData);
        const lteIntervalWithBuffer = Number(fetchedData?.lteInterval + fetchedData?.buffer) || 125;
        setPVitalInterval(lteIntervalWithBuffer*60*1000);
        console.log(fetchedData, 'FIREBASE STATIC DATA');
      });
  
      return () => unsubscribe();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("visibilitychange", onTabChange);
    return () => {
      window.removeEventListener('visibilitychange', onTabChange);
    };
  }, [drawer])

  let tempTimeout = '';
  const onTabChange = () => {
    clearTimeout(tempTimeout);
    tempTimeout = setTimeout(() => {
      if (drawer) {
        if (document.visibilityState !== "visible") {
          clearInterval(drawerRef.current);
          clearInterval(svittalDrawerRef.current);
        }
      }
    }, 500);

    return () => {
      clearTimeout(tempTimeout);
    };
  }


  const fetchButtonTapped = () => {
    const isValidDate = moment(userInfo.intervalStartTime, "MM/DD/YYYY h:mm a", true).isValid();
    if (isValidDate) {
      clearInterval(drawerRef.current);
      clearInterval(svittalDrawerRef.current);
      setTimeout(() => {
        resetGraphData();
      }, 3000);
      startGraphLoader();
      setTimeout(() => {
        intervalChange(userTimeZone, true, userInfo.interval, userInfo.devID);
      }, 5000);
    } else {
      setToastStatus(true);
      setAlertMessage("Please enter Start Time in MM/DD/YYYY h:mm a format (Example: 04/27/2022 1:14 pm)");
    }
  }

  const changeLocalDevicePosition = (flag = false) => {
    let userGraphDataNew = {};
    let userGraphData = device?.graphData[0]?.linkedGraphData;
    const savedSensorPositionStr = userWithOrder?.data?.Sensorposition?.value;
    if (userGraphData && savedSensorPositionStr) {
      const sensorsArr = savedSensorPositionStr.split(';');
      let savedSensorPositionArr = sensorsArr.sort((a, b) => { return a.split('/')[1] - b.split('/')[1] });

      for (var i = 0; i < savedSensorPositionArr.length; i++) {
        for (let ele in userGraphData) {
          if (savedSensorPositionArr[i].split('/')[0] === ele) {
            userGraphDataNew[ele] = userGraphData[ele];
          }
        }
      }

      /* || This loop to excecute in case new sensor added after order set into DB || */
      for (let ele in userGraphData) {
        if (!savedSensorPositionStr.includes(ele)) {
          userGraphDataNew[ele] = userGraphData[ele];
        }
      }

      device.graphData[0].linkedGraphData = userGraphDataNew;
      device.loadingData = false;
    }

    if (!savedSensorPositionStr) {
      device.loadingData = false;
    }

    if (flag && userGraphData && !device.loadingData) {
      setShowSensors(true);
    }

    if (loggedUserRole === "Guest") {
      filterGuestSensors();
    }
  }

  /* This function will be called for only user having GUEST Role, to filter out sensors which he has access */
  const filterGuestSensors = () => {
    let tempGraphData = {};
    if (guestSensors.length && device?.graphData?.length && Object.keys(device?.graphData[0]?.linkedGraphData).length) {
      for (let i = 0; i < guestSensors.length; i++) {
        for (let key in device?.graphData[0].linkedGraphData) {
          if (guestSensors[i]?.macId !== "all" && key === guestSensors[i]?.macId) {
            tempGraphData[key] = device?.graphData[0].linkedGraphData[key];
          }
        }
      }
      device.graphData[0].linkedGraphData = tempGraphData;
      device.loadingData = false;
    }
  }

  const getDeviceData = (_filterData = {}) => {
    setFirstPVitalHit(true);
    setShowSensors(false);
    setCurrentPage(1);
    setLastFilter({});
    getSensorsOrderData();

    let supplierId = userInfo?.supplierId;
    if (!userInfo?.supplierId) {
      let obj = { supplierId: loggedUserSupplier ? loggedUserSupplier?.supplierId : companyList[0].supplierId };
      supplierId = loggedUserSupplier ? loggedUserSupplier?.supplierId : companyList[0].supplierId;
      setUserInfo({ ...userInfo, ...obj });
    }

    // Clear intervals before proceeding
    clearInterval(timerToClearSomewhere.current);
    clearInterval(devTimerToClearSomewhere.current);
    clearInterval(svittalDashboardDrawerRef.current);

    if (!userInfo?.deviceId || !supplierId) {
      setAlertMessage("Device and company both are mandatory");
      setToastStatus(true);
    } else {
      let m = moment();
      const rangeStartDateTime = m.utc().subtract(60, "minutes").format("YYYY-MM-DDTHH:mm:ss");
      const rangeEndDateTime = moment().utc().format("YYYY-MM-DDTHH:mm:ss");
      let ownerFilter = {};
      if (loggedUserRole === "Patient") {
        ownerFilter = { users: [loggedUserId] };
      } else {
        if (userInfo.userId === "all" && loggedUserRole !== "Guest") {
          ownerFilter = {
            users: usersList.reduce((acc, val) => {
              if (val?.defaultRole?.supplier?.supplierId === userInfo?.supplierId) {
                return [...acc, val.userId];
              }
              return acc;
            }, [])
          };
        } else {
          ownerFilter = { users: userInfo?.userId && userInfo?.userId !== "all" ? [userInfo?.userId] : [] };
        }
      }

      setStartTime(rangeStartDateTime);
      let selectedTime = moment.utc(rangeStartDateTime).tz(userTimeZone).format("MM/DD/YYYY h:mm a");

      handleStartDateChange(selectedTime);

      setEndTime(rangeEndDateTime);

      let finalObj = {
        deviceDataModelId: userInfo.deviceId,
        deviceName: userInfo.deviceName,
        supplierId: supplierId,
        devicePropertyCodes: ["Hr", "Rr", "Stress", "Motion", "DevidPvital", "FriendlyNameP", "LocationP", "Offline", "RssRrMv", "RssHrMv", "Sdnn", "Rmssd", "Occupied", "Online"],
        ownerFilter,
        deviceCriteria: [{ key: "Timestamp", operator: "Between", valueFrom: rangeStartDateTime, valueTo: rangeEndDateTime }],
      };

      if (userInfo.friendlyName !== "all" && userInfo.friendlyName) {
        finalObj.macId = userInfo.friendlyName;
        finalObj.deviceCriteria = [
          ...finalObj.deviceCriteria,
          { key: "DevidPvital", operator: "Equal", value: userInfo.friendlyName},
        ];
      }
      let dhObj1 = {
        deviceDataModelId: `${userInfo.deviceId}-dh`,
        aggregationPropertyOptions: [
          { propertyCode: "Hwvers", aggregationType: "Last" },
          { propertyCode: "Fwvers", aggregationType: "Last" },
          { propertyCode: "WifiRss", aggregationType: "Last" },
          { propertyCode: "DevidDevhealth", aggregationType: "First" },
          { propertyCode: "RouterMac1", aggregationType: "Last" },
          { propertyCode: "RouterMac2", aggregationType: "Last" }
        ],

        deviceCriteriaGroup: {
          isOr: false,
          groupOwner: null,
          groupElements: [{
            isOr: false,
            groupOwner: null,
            groupElements: [{
              key: "TimestampD",
              operator: "Between",
              valueFrom: moment(rangeEndDateTime).subtract(1, "year").format("YYYY-MM-DDTHH:mm:ss"),
              valueTo: rangeEndDateTime
            }]
          }],
        },
        groupingPropertyOptions: [{ propertyCode: "DevidDevhealth" }],
        firstOrLastAggregationTypeSortBy: "TimestampD",
        ownerFilter: ownerFilter,
      };
      let dhObj = { ...finalObj };
      delete dhObj.deviceCriteria;
      dhObj.deviceCriteria = [{
        key: "TimestampD",
        operator: "Between",
        valueFrom: moment(rangeEndDateTime).subtract(1, "year").format("YYYY-MM-DDTHH:mm:ss"),
        valueTo: rangeEndDateTime,
      }];
      dhObj.deviceId = `${userInfo.deviceId}-dh`;
      dhObj.devicePropertyCodes = ["DevidDevhealth", "Hwvers", "Fwvers", "WifiRss", "RouterMac1", "RouterMac2"];

      if (userInfo.friendlyName !== "all" && userInfo.friendlyName) {
        dhObj1.deviceCriteriaGroup.groupElements[0].groupElements = [
          ...dhObj1.deviceCriteriaGroup.groupElements[0].groupElements,
          { key: "DevidDevhealth", operator: "Equal", value: userInfo.friendlyName },
        ];
        dhObj.deviceCriteria = [
          ...dhObj.deviceCriteria,
          { key: "DevidDevhealth", operator: "Equal", value: userInfo.friendlyName },
        ];
      }
      let svObj = { ...finalObj };
      delete svObj.deviceCriteria;
      svObj.deviceCriteria = [
        { key: "TimestampSv", operator: "Between", valueFrom: rangeStartDateTime, valueTo: rangeEndDateTime },
        { key: "Apneaperminute", operator: "IsSet", value: null, isDateValue: false },
        { key: "Occupied", operator: "IsSet", value: null, isDateValue: false },
      ];
      svObj.deviceDataModelId = `${userInfo.deviceId}-sv`;
      svObj.devicePropertyCodes = ["DevidSvital", "Occupied", "Apneaperminute"];
      if (userInfo.friendlyName !== "all" && userInfo.friendlyName) {
        svObj.deviceCriteria = [
          ...svObj.deviceCriteria,
          { key: "DevidSvital", operator: "Equal", value: userInfo.friendlyName },
        ];
      }
      getDeviceDataViewList({
        pVittal: finalObj,
        devHealth: dhObj1,
        sVittal: svObj,
      })
      getDeviceDataList({
        pVittal: finalObj,
        devHealth: dhObj1,
        sVittal: svObj,
      });
      setLastFilter({ pVittal: finalObj, devHealth: dhObj1 });
    }
    getParamsDetails();
    getThresholdData();
  };

  useEffect(() => {
    if (device.updateSensorStatus) {
      getDeviceData();
      setShowSensorControl(false);
    }
  }, [device.updateSensorStatus]);

  if (nextCall && device?.graphData?.length) {
    if (sensor?.userId) {
      const currentUser = device?.graphData.find((val) => val.userId === sensor.userId);
      const HRValues = [],
        RRValues = [],
        Timings = [];

      if (currentUser?.graphData?.length) {
        currentUser.graphData.forEach((val) => {
          Timings.push(val.data.Hr.valueProvidedOn);
          HRValues.push([new Date(val.data.Hr.valueProvidedOn), val.data.Hr.value]);
          RRValues.push([new Date(val.data.Hr.valueProvidedOn), val.data.Rr.value]);
        });
      } else {
        const currentTime = moment().utc().format("YYYY-MM-DDTHH:mm:ss");
        HRValues.push([new Date(currentTime), 0]);
        RRValues.push([new Date(currentTime), 0]);
      }
    }
    setNextCall(false);
  }

  useEffect(() => {
    if (device?.graphData?.length) {
      let apiRequest = apiRequestCount;

      const fetchPVitalData = () => {
        let ownerFilter = {};
        if (loggedUserRole === "Patient") {
          ownerFilter = { users: [loggedUserId] };
        } else {
          if (userInfo.userId === "all" && loggedUserRole !== "Guest") {
            ownerFilter = {
              users: usersList.reduce((acc, val) => {
                if (val?.defaultRole?.supplier?.supplierId === userInfo?.supplierId) {
                  return [...acc, val.userId];
                }
                return acc;
              }, []),
            };
          } else {
            ownerFilter = { users: userInfo.userId && userInfo.userId !== "all" ? [userInfo.userId] : [] };
          }
        }

        let currentTime = moment().utc().format("YYYY-MM-DDTHH:mm:ss");
        let m = moment();
        const lteIntervalWithBuffer = (firebaseDBData?.lteInterval + firebaseDBData?.buffer) || 125;
        const rangeStartDateTime = m.utc().subtract(lteIntervalWithBuffer, "minutes").format("YYYY-MM-DDTHH:mm:ss");
        setNextCall(true);
        let finalObj = {
          deviceDataModelId: userInfo.deviceId,
          supplierId: userInfo.supplierId,
          devicePropertyCodes: ["Hr", "Rr", "Stress", "Motion", "DevidPvital", "FriendlyNameP", "LocationP", "Offline", "RssRrMv", "RssHrMv", "Sdnn", "Rmssd", "Occupied", "Online"],
          ownerFilter,
        };
        let dhObj = { ...finalObj };
        dhObj.deviceId = `${userInfo.deviceId}-dh`;
        dhObj.devicePropertyCodes = ["DevidDevhealth", "Hwvers", "Fwvers", "WifiRss", "RouterMac1", "RouterMac2"];
        dhObj.deviceCriteria = [{ key: "TimestampD", operator: "Between", valueFrom: rangeStartDateTime, valueTo: currentTime }];
        finalObj.deviceCriteria = [{ key: "Timestamp", operator: "Between", valueFrom: rangeStartDateTime, valueTo: currentTime }];
        if (userInfo.friendlyName !== "all" && userInfo.friendlyName) {
          finalObj.deviceCriteria = [
            ...finalObj.deviceCriteria,
            {
              key: "DevidPvital",
              operator: "Equal",
              value: userInfo.friendlyName,
            },
          ];
          dhObj.deviceCriteria = [
            ...dhObj.deviceCriteria,
            {
              key: "DevidDevhealth",
              operator: "Equal",
              value: userInfo.friendlyName,
            },
          ];
        }
        const sensorsMacAddress = Object.keys(device?.graphData?.[0]?.linkedGraphData);
        finalObj.sensorsMacAddress = sensorsMacAddress;
        const filterRequest = { pVittal: finalObj };
        newDataRequest(filterRequest);
        if (drawer) {
          if (apiRequest > 8) {
            apiRequest = 0 + 1;
            setApiRequestCount(apiRequest);
          } else {
            apiRequest = apiRequest + 1;
            setApiRequestCount(apiRequest);
          }
        }
        setTimeout(() => {
          setCsvDownloadReady(true);
        }, 3000);
      }

      const devHealthData = () => {
        let ownerFilter = {};
        if (loggedUserRole === "Patient") {
          ownerFilter = { users: [loggedUserId] };
        } else {
          if (userInfo.userId === "all") {
            ownerFilter = {
              users: usersList.reduce((acc, val) => {
                if (val.defaultRole?.supplier?.supplierId === userInfo.supplierId) {
                  return [...acc, val.userId];
                }
                return acc;
              }, []),
            };
          } else {
            ownerFilter = { users: userInfo.userId && userInfo.userId !== "all" ? [userInfo.userId] : [] };
          }
        }
        let currentTime = moment().utc().format("YYYY-MM-DDTHH:mm:ss");
        var m = moment();
        const rangeStartDateTime = m.utc().subtract(2, "minutes").format("YYYY-MM-DDTHH:mm:ss");
        setNextCall(true);
        let dhObj1 = {
          deviceDataModelId: `${userInfo.deviceId}-dh`,
          aggregationPropertyOptions: [
            { propertyCode: "Hwvers", aggregationType: "Last" },
            { propertyCode: "Fwvers", aggregationType: "Last" },
            { propertyCode: "WifiRss", aggregationType: "Last" },
            { propertyCode: "DevidDevhealth", aggregationType: "First" },
            { propertyCode: "RouterMac1", aggregationType: "Last" },
            { propertyCode: "RouterMac2", aggregationType: "Last" }
          ],
          deviceCriteriaGroup: {
            isOr: false,
            groupOwner: null,
            groupElements: [
              {
                isOr: false,
                groupOwner: null,
                groupElements: [
                  {
                    key: "TimestampD",
                    operator: "Between",
                    valueFrom: moment(currentTime)
                      .subtract(1, "year")
                      .format("YYYY-MM-DDTHH:mm:ss"),
                    valueTo: currentTime,
                  },
                ],
              },
            ],
          },
          groupingPropertyOptions: [
            {
              propertyCode: "DevidDevhealth",
            },
          ],
          firstOrLastAggregationTypeSortBy: "TimestampD",
          ownerFilter: ownerFilter,
        };
        let dhObj = {
          deviceDataModelId: `${userInfo.deviceId}-dh`,
          devicePropertyCodes: ["DevidDevhealth", "Hwvers", "Fwvers", "WifiRss", "RouterMac1", "RouterMac2"],
          supplierId: userInfo.supplierId,
          deviceCriteria: [
            {
              key: "TimestampD",
              operator: "Between",
              valueFrom: rangeStartDateTime,
              valueTo: currentTime,
            },
          ],
          ownerFilter,
        };

        if (userInfo.friendlyName !== "all" && userInfo.friendlyName) {
          dhObj1.deviceCriteriaGroup.groupElements[0].groupElements = [
            ...dhObj1.deviceCriteriaGroup.groupElements[0].groupElements,
            {
              key: "DevidDevhealth",
              operator: "Equal",
              value: userInfo.friendlyName,
            },
          ];
          dhObj.deviceCriteria = [
            ...dhObj.deviceCriteria,
            {
              key: "DevidDevhealth",
              operator: "Equal",
              value: userInfo.friendlyName,
            },
          ];
        }
        const filterRequest = { devHealth: dhObj1 };
        newDevDataRequest(filterRequest);
      }

      const sVitalData = () => {
        let ownerFilter = {};
        if (loggedUserRole === "Patient") {
          ownerFilter = { users: [loggedUserId] };
        } else {
          if (userInfo.userId === "all") {
            ownerFilter = {
              users: usersList.reduce((acc, val) => {
                if (val.defaultRole?.supplier?.supplierId === userInfo.supplierId) {
                  return [...acc, val.userId];
                }
                return acc;
              }, []),
            };
          } else {
            ownerFilter = {
              users:
                userInfo.userId && userInfo.userId !== "all"
                  ? [userInfo.userId]
                  : [],
            };
          }
        }
        let currentTime = moment().utc().format("YYYY-MM-DDTHH:mm:ss");
        var m = moment();
        const rangeStartDateTime = m.utc().subtract(2, "minutes").format("YYYY-MM-DDTHH:mm:ss");
        setNextCall(true);
        let svObj = {
          deviceDataModelId: `${userInfo.deviceId}-sv`,
          devicePropertyCodes: ["DevidSvital", "Occupied", "Apneaperminute"],
          supplierId: userInfo.supplierId,
          deviceCriteria: [
            { key: "TimestampSv", operator: "Between", valueFrom: rangeStartDateTime, valueTo: currentTime },
            { key: "Apneaperminute", operator: "IsSet", value: null, isDateValue: false },
            { key: "Occupied", operator: "IsSet", value: null, isDateValue: false },
          ],
          ownerFilter,
        };

        if (userInfo.friendlyName !== "all" && userInfo.friendlyName) {
          svObj.deviceCriteria = [
            ...svObj.deviceCriteria,
            { key: "DevidSvital", operator: "Equal", value: userInfo.friendlyName },
          ];
        }
        newSvDataRequest(svObj);
      }

      if(firstPVitalHit) {
        setFirstPVitalHit(false);
        setTimeout(() => {
          fetchPVitalData();
        }, 4000);
        setTimeout(() => {
          devHealthData();
        }, 2000);
      }

      timerToClearSomewhere.current = setInterval(() => {
        if(document.visibilityState === "visible") {
          fetchPVitalData()
        }
      }, pVitalInterval);
    }
    changeLocalDevicePosition();

    return () => {
      clearInterval(timerToClearSomewhere.current);
      clearInterval(devTimerToClearSomewhere.current);
      clearInterval(svittalDashboardDrawerRef.current);
    };
  }, [device?.graphData?.length]);

  if (
    device.backDateData &&
    device.rangeStartDateTime &&
    device.rangeEndDateTime &&
    fetchGraphDataCall &&
    device.graphArray.hrValue.length > 4
  ) {
    let startTime = moment.utc(device.rangeStartDateTime).tz(userTimeZone).format("MM/DD/YYYY h:mm:ss a");

    handleStartDateChange(startTime);
    setStartTime(device.rangeStartDateTime);

    let newEndTime = moment.utc(device.rangeEndDateTime).tz(userTimeZone).format("MM/DD/YYYY h:mm:ss a");
    handleEndDateChange(newEndTime);
    setEndTime(device.rangeEndDateTime);

    setFetchGraphDataCall(false);
  }

  const intervalChange = (
    currentUserTimeZone,
    time,
    interval,
    devId,
    userId,
    userOffset,
    dlSaving
  ) => {
    devId && setDevDataId(devId);
    let m = moment.utc().tz(UTC_TIME_ZONE);
    let currentEndDate = moment().utc().format("YYYY-MM-DDTHH:mm:ss");
    if (userInfo.startTime && time) {
      const selectedTimeZone = moment(selectedStartDate).format("YYYY-MM-DDTHH:mm:ss");
      m = moment.tz(selectedTimeZone, currentUserTimeZone).utc();
    } else if (device.backDateData && time) {
      const selectedTimeZone = moment(selectedStartDate).format("YYYY-MM-DDTHH:mm:ss");
      m = moment.tz(selectedTimeZone, currentUserTimeZone).utc();
      // currentEndDate = moment.tz(endTime, UTC_TIME_ZONE).utc().format("YYYY-MM-DDTHH:mm:ss");
    }
    let rangeStartDateTime = m.format("YYYY-MM-DDTHH:mm:ss");
    let rangeEndDateTime = moment().utc().format("YYYY-MM-DDTHH:mm:ss");
    const intervalArray = interval.split(" ");

    if (interval === "Weekly") {
      m.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      rangeStartDateTime =
        (userInfo.startTime && time) || (device.backDateData && time)
          ? m.format("YYYY-MM-DDTHH:mm:ss")
          : m.subtract(7, "day").utc().format("YYYY-MM-DDTHH:mm:ss");

      rangeEndDateTime =
        (userInfo.startTime && time) || (device.backDateData && time)
          ? m.add(7, "day").format("YYYY-MM-DDTHH:mm:ss")
          : currentEndDate;
    } else if (interval === "Monthly") {
      m.set({ date: 1, hour: 0, minute: 0, second: 0, millisecond: 0 });
      rangeStartDateTime =
        (userInfo.startTime && time) || (device.backDateData && time)
          ? m.format("YYYY-MM-DDTHH:mm:ss")
          : m.subtract(30, "day").utc().format("YYYY-MM-DDTHH:mm:ss");
      rangeEndDateTime =
        (userInfo.startTime && time) || (device.backDateData && time)
          ? m.add(30, "day").format("YYYY-MM-DDTHH:mm:ss")
          : currentEndDate;
    } else if (intervalArray[1] === "Hr") {
      rangeStartDateTime =
        (userInfo.startTime && time) || (device.backDateData && time)
          ? m.format("YYYY-MM-DDTHH:mm:ss")
          : m
            .subtract(intervalArray[0], "h")
            .utc()
            .format("YYYY-MM-DDTHH:mm:ss");
      rangeEndDateTime =
        (userInfo.startTime && time) || (device.backDateData && time)
          ? m.add(intervalArray[0], "h").format("YYYY-MM-DDTHH:mm:ss")
          : currentEndDate;
    } else if (intervalArray[1] === "secs") {
      rangeStartDateTime =
        (userInfo.startTime && time) || (device.backDateData && time)
          ? moment(userInfo.intervalStartTime).format("YYYY-MM-DDTHH:mm:ss")
          : m.subtract(intervalArray[0], "seconds").utc().format("YYYY-MM-DDTHH:mm:ss");
      rangeEndDateTime =
        (userInfo.startTime && time) || (device.backDateData && time)
          ? m.add(intervalArray[0], "seconds").format("YYYY-MM-DDTHH:mm:ss")
          : currentEndDate;
    } else {
      rangeStartDateTime =
        (userInfo.startTime && time) || (device.backDateData && time)
          ? m.format("YYYY-MM-DDTHH:mm:ss")
          : m.subtract(intervalArray[0], "minutes").utc().format("YYYY-MM-DDTHH:mm:ss");
      rangeEndDateTime = (userInfo.startTime && time) || (device.backDateData && time)
        ? m.add(intervalArray[0], "minutes").format("YYYY-MM-DDTHH:mm:ss") : currentEndDate;
    }
    setStartTime(rangeStartDateTime);
    let selectedTime = moment.utc(rangeStartDateTime).tz(currentUserTimeZone).format("MM/DD/YYYY h:mm a");

    handleStartDateChange(selectedTime);
    setEndTime(rangeEndDateTime);

    let graphRequest = GRAPH_DATA_REQUEST;
    graphRequest.deviceDataModelId = userInfo.deviceId;
    graphRequest.deviceCriteria[1].valueFrom = rangeStartDateTime;
    graphRequest.deviceCriteria[1].valueTo = rangeEndDateTime;
    graphRequest.ownerFilter.users = [userId || sensor.userId];
    graphRequest.offset = userOffset || offset;
    graphRequest.currentUserTimeZone = currentUserTimeZone;
    graphRequest.dayLightSaving = dlSaving || dayLightSaving;
    graphRequest.backDateData = device.backDateData || false;
    if (devId) {
      graphRequest.deviceCriteria[0].value = devId;
    }
    if (
      interval.split(" ")[1] === "secs" ||
      interval.split(" ")[1] === "mins"
    ) {
      graphRequest.options.groupType = "Second";
    }
    graphRequest.interval = userInfo.interval;
    let obj = {
      deviceDataModelId: userInfo.deviceId,
      // rangeStartDateTime: rangeStartDateTime,
      // rangeEndDateTime: rangeEndDateTime,
      supplierId: userInfo.supplierId,
      ownerFilter: {
        users: [userId || sensor.userId],
      },
      offset: userOffset || offset,
      abbr,
      dayLightSaving: dlSaving || dayLightSaving,
      currentUserTimeZone: currentUserTimeZone,
      backDateData: device.backDateData || false,
      devicePropertyCodes: ["Hr", "Rr", "Stress", "Motion", "DevidPvital", "FriendlyNameP", "LocationP", "RssHrMv", "Occupied"],
      deviceCriteria: graphRequest.deviceCriteria,
    };
    if (devId) {
      obj["devId"] = devId;
    }
    if (interval === "1 min" || interval === "2 mins" || interval === "5 mins" || interval === "10 mins" || interval === "30 mins") {
      fetchUserMinDeviceData(obj);
    } else {
      graphRequest.options.groupType = "Minute";
      delete obj.rangeStartDateTime;
      delete obj.rangeEndDateTime;
      fetchUserDeviceData({ ...graphRequest, dataPropertyCode: "Hr", deviceData: obj });
      fetchUserDeviceData({ ...graphRequest, dataPropertyCode: "Rr", deviceData: obj });
      fetchUserDeviceData({ ...graphRequest, dataPropertyCode: "Stress", deviceData: obj });
      fetchUserDeviceData({ ...graphRequest, dataPropertyCode: "Motion", deviceData: obj });
    }
    let svittalObj = {
      deviceDataModelId: "d36a4373-fbdc-44a3-8c96-4bb920041e40-sv",
      devicePropertyCodes: ["DevidSvital", "Apneaperminute", "Occupied"],
      supplierId: userInfo.supplierId,
      deviceCriteria: [
        { key: "TimestampSv", operator: "Between", valueFrom: rangeStartDateTime, valueTo: rangeEndDateTime },
        { key: "DevidSvital", operator: "Equal", value: devId },
        { key: "Apneaperminute", operator: "IsSet", value: null, isDateValue: false },
        { key: "Occupied", operator: "IsSet", value: null, isDateValue: false },
      ],
      ownerFilter: { users: [userId || sensor.userId] },
    };
    if (loggedUserRole === "Patient") {
      let paramObj = {
        deviceDataModelId: "d36a4373-fbdc-44a3-8c96-4bb920041e40-pa",
        devicePropertyCodes: ["DevidParams", "Csv1sec"],
        supplierId: userInfo.supplierId,
        deviceCriteria: [{
          key: "DevidParams",
          operator: "Equal",
          value: devId,
        }],
        ownerFilter: {
          users: [userId || sensor.userId],
        },
      };
      paramsDataRequest(paramObj);
    }
    svittalDataRequest(svittalObj);
    setFetchGraphDataCall(true);
    setApiRequestCount(1);
  };

  const exportPdf = (type, name) => {
    const data = document.querySelector(`#capture`);
    
    // If in HR/RR view mode, temporarily hide Stress and Motion charts
    if (viewMode === 'hrrr') {
      const stressCharts = data.querySelectorAll('[data-chart-type="stress"], [data-chart-type="motion"]');
      const stressMotionSections = data.querySelectorAll('.stress-chart, .motion-chart');
      
      // Hide Stress and Motion charts
      [...stressCharts, ...stressMotionSections].forEach(element => {
        if (element) {
          element.style.display = 'none';
        }
      });
    }
    
    html2canvas(data, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    }).then((canvas) => {
      let dataURL = canvas.toDataURL("image/jpeg", 0.95);
      if (type === "pdf") {
        const pdf = new jsPDF("l", "mm", "a4");
        
        // Calculate dimensions to fit full width
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 10;
        const contentWidth = pageWidth - (2 * margin);
        const contentHeight = pageHeight - (2 * margin);
        
        // Calculate aspect ratio to maintain proportions
        const canvasAspectRatio = canvas.width / canvas.height;
        const pageAspectRatio = contentWidth / contentHeight;
        
        let finalWidth, finalHeight;
        if (canvasAspectRatio > pageAspectRatio) {
          // Canvas is wider, fit to width
          finalWidth = contentWidth;
          finalHeight = contentWidth / canvasAspectRatio;
        } else {
          // Canvas is taller, fit to height
          finalHeight = contentHeight;
          finalWidth = contentHeight * canvasAspectRatio;
        }
        
        // Center the image
        const x = (pageWidth - finalWidth) / 2;
        const y = (pageHeight - finalHeight) / 2;
        
        pdf.addImage(dataURL, "JPEG", x, y, finalWidth, finalHeight);
        pdf.save(`${name}.pdf`);
        setDisablePdfBtn(false);
      } else if (type === "png") {
        var link = document.createElement("a");
        link.download = `${name}.png`;
        link.href = dataURL;
        link.click();
      }
      
      // Restore Stress and Motion charts visibility if they were hidden
      if (viewMode === 'hrrr') {
        const stressCharts = data.querySelectorAll('[data-chart-type="stress"], [data-chart-type="motion"]');
        const stressMotionSections = data.querySelectorAll('.stress-chart, .motion-chart');
        
        [...stressCharts, ...stressMotionSections].forEach(element => {
          if (element) {
            element.style.display = '';
          }
        });
      }
    });
  };

  const onIntervalChange = (interval) => {
    let m = moment(selectedEndDate);
    let rangeStartDateTime = m.format("YYYY-MM-DDTHH:mm:ss");
    if (interval === "Weekly") {
      m.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      rangeStartDateTime = m.subtract(7, "day").format("MM/DD/YYYY h:mm a");
    } else if (interval === "Monthly") {
      m.set({ date: 1, hour: 0, minute: 0, second: 0, millisecond: 0 });
      rangeStartDateTime = m.subtract(30, "day").format("MM/DD/YYYY h:mm a");
    } else if (interval === "1 Hr") {
      rangeStartDateTime = m.subtract(1, "h").format("MM/DD/YYYY h:mm a");
    } else if (interval === "2 Hr") {
      rangeStartDateTime = m.subtract(2, "h").format("MM/DD/YYYY h:mm a");
    } else if (interval === "4 Hr") {
      rangeStartDateTime = m.subtract(4, "h").format("MM/DD/YYYY h:mm a");
    } else if (interval === "6 Hr") {
      rangeStartDateTime = m.subtract(6, "h").format("MM/DD/YYYY h:mm a");
    } else if (interval === "8 Hr") {
      rangeStartDateTime = m.subtract(8, "h").format("MM/DD/YYYY h:mm a");
    } else if (interval === "10 Hr") {
      rangeStartDateTime = m.subtract(10, "h").format("MM/DD/YYYY h:mm a");
    } else if (interval === "18 Hr") {
      rangeStartDateTime = m.subtract(18, "h").format("MM/DD/YYYY h:mm a");
    } else if (interval === "12 Hr") {
      rangeStartDateTime = m.subtract(12, "h").format("MM/DD/YYYY h:mm a");
    } else if (interval === "24 Hr") {
      rangeStartDateTime = m.subtract(24, "h").format("MM/DD/YYYY h:mm a");
    } else {
      const intervalArray = interval.split(" ");
      rangeStartDateTime = m
        .subtract(intervalArray[0], "minutes")
        .format("MM/DD/YYYY h:mm a");
    }

    handleStartDateChange(rangeStartDateTime);
  };

  const selectedGroupType = (interval) => {
    var currentGroupType = "Second";
    switch (interval) {
      case "1 min":
      case "2 mins":
      case "5 mins":
      case "10 mins":
      case "30 mins":
        currentGroupType = "Second";
        break;
      case "1 Hr":
      case "2 Hr":
      case "4 Hr":
      case "6 Hr":
      case "8 Hr":
      case "10 Hr":
      case "12 Hr":
      case "18 Hr":
      case "24 Hr":
        currentGroupType = "Minute";
        break;
      case "Daily":
      case "Weekly":
      case "Monthly":
        currentGroupType = "Hour";
        break;
      default:
        currentGroupType = "Second";
        break;
    }
    return currentGroupType;
  };

  const exportCSV = (data, group) => {
    var currentGroupType = group ? group : selectedGroupType(userInfo.interval);
    
    // Base aggregation options that are always included
    const baseAggregationOptions = [
      { propertyCode: "Hr", aggregationType: "Average" },
      { propertyCode: "Rr", aggregationType: "Average" },
      { propertyCode: "RssRrMv", aggregationType: "Average" },
      { propertyCode: "RssHrMv", aggregationType: "Average" }
    ];

    // Add Stress and Motion only if not in HR/RR view mode
    const stressMotionOptions = [
      { propertyCode: "Stress", aggregationType: "Average" },
      { propertyCode: "Motion", aggregationType: "Average" },
    ];

    let aggregationPropertyOptions = viewMode === 'hrrr' ? baseAggregationOptions : 
    [...baseAggregationOptions.slice(0, 2), ...stressMotionOptions, ...baseAggregationOptions.slice(2)];

    if(["TenantAdmin", "TenantUser"].includes(loggedUserRole)) {
      aggregationPropertyOptions = [...baseAggregationOptions.slice(0, 2), ...stressMotionOptions, ...baseAggregationOptions.slice(2), 
        { propertyCode: "HrTd", aggregationType: "Average" },
        { propertyCode: "HrFd", aggregationType: "Average" },
        { propertyCode: "HrSel", aggregationType: "Average" },
        { propertyCode: "HrEst", aggregationType: "Average" },
        { propertyCode: "WinFd", aggregationType: "Average" },
        { propertyCode: "WinSel", aggregationType: "Average" },
        { propertyCode: "WinEst", aggregationType: "Average" },
        { propertyCode: "WinTd", aggregationType: "Average" },
      ];
    }

    let obj = {
      deviceCriteriaGroup: {
        groupElements: [{
          key: "Timestamp",
          operator: "Between",
          valueFrom: startTime,
          valueTo: endTime,
        }, {
          key: "DevidPvital",
          operator: "Equal",
          value: devDataId,
        }],
      },
      deviceDataModelId: "d36a4373-fbdc-44a3-8c96-4bb920041e40",
      aggregationPropertyOptions,
      groupingPropertyOptions: [{
        propertyCode: "Timestamp",
        groupType: currentGroupType,
      }],
      ownerFilter: { users: [sensor.userId] },
      offset: offset,
      abbr,
      DevidPvital: devDataId,
      friendlyName,
      swVersion: data.fwVersion,
      hwVersion: data.hwVersion,
      dayLightSaving: dayLightSaving,
      fullName: sensor.fullName,
      userRole: loggedUserRole,
      currentUserTimeZone: userTimeZone,
      type: "csv",
      group: group || userInfo.interval === "1 min" ? true : false,
      groupBy: `${currentGroupType}-Timestamp`,
      groupBySv: `${currentGroupType}-TimestampSv`,
      interval: userInfo.interval,
      viewMode: viewMode,
    };
    fetchCsvData(obj);
    obj.rangeStartDateTime = startTime;
    group && fetchMinCsvData(obj);
  };

  const onDrawerClose = () => {
    setDrawer(false);
    clearInterval(drawerRef.current);
    clearInterval(svittalDrawerRef.current);
    resetGraphData();
    setUserTimeZone(UTC_TIME_ZONE);
    setUserMacAddress();
    let obj = { startTime: false, };
    setUserInfo({ ...userInfo, ...obj });
    setSensor({});
  };

  /* Fetch User Profile data on User Selection change */
  const getSensorsOrderData = async() => {
    if (userInfo?.userId && userInfo?.userId !== "all") {
      const endPoint = `${API_BASE_URL}/data/devicedata-advanced?pageSize=1&sortOrder=DESC`;
      const payload = {
        deviceDataModelId: "6df2d80f-be28-47ac-a509-8f0b6aa3bbb8",
        deviceCriteria: [{
          "key": "Userid",
          "operator": "Equal",
          "value": userInfo?.userId
        }]
      }
      const data = await fetchRequest("POST", endPoint, payload);
      setSensorsOrderData(data?.content);
    }
  }

  useEffect(() => {
    if (device?.userSensorPosSaved) {
      getSensorsOrderData();
      setIsSensorPositionSaved(true);
    }
  }, [device?.userSensorPosSaved]);

  /* To check wheather data is saved for current user or not */
  useEffect(() => {
    if (sensorsOrderData?.length && userInfo?.userId) {
      const matchedUserData = sensorsOrderData.find(ele => ele?.data?.Userid?.value === userInfo?.userId);
      if (matchedUserData && Object.keys(matchedUserData).length) {
        setUserWithOrder(matchedUserData);
        getDeviceDataById(matchedUserData?.deviceDataModelId);
        if (isSensorPositionSaved) {
          setTimeout(() => {
            changeLocalDevicePosition();
          }, 500);
          setIsSensorPositionSaved(false);
        }
      } else {
        setUserWithOrder({});
      }
    }
  }, [sensorsOrderData, userInfo.userId]);

  /* Updating Device Data in the List as per Sensor Position data Saved from DB */
  useEffect(() => {
    changeLocalDevicePosition(true);
    const userDetails = localStorage.getItem("user-details"),
      isPatient = userDetails && JSON.parse(userDetails)?.roles?.[0]?.role.toLowerCase() === "patient" || false;
    if (isPatient && !firebaseInitilize) {
      const deviceIds = device?.graphData?.[0]?.deviceIds;
      if(deviceIds?.length) {
        const enabledAlertActiveDevices = deviceIds.filter(sensor => 
          sensor.EnableAlerts?.value === "Yes" && sensor.Active?.value === "Yes"
        ).map(sensor => sensor.Devid.value);
        const formattedMacs = enabledAlertActiveDevices.map(mac => mac.replace(/:/g, "-") + "-Alerts");
        if(formattedMacs?.length) {
          setTimeout(() => {
            console.log("[FIREBASE] - User role is Patient, Push notification Work started from here");
            firebaseMessaging(formattedMacs);
            setFirebaseInitilize(true);
          }, 1500);
        }
      }
    }
  }, [device.graphData]);

  /* To update the position of Sensor into DB */
  const onIndexUpdate = (val, obj, currEle) => {
    let Sensorposition = "";
    let cloneLinkedGD = JSON.parse(JSON.stringify(obj.linkedGraphData));
    /* Spare task */
    for (let i in cloneLinkedGD) {
      delete cloneLinkedGD[i].data
    }
    /* Spare task */

    delete cloneLinkedGD[currEle];

    let counter = 0;
    for (let i = 0; i < Object.keys(cloneLinkedGD).length; i++) {
      counter = counter + 1;
      if (parseInt(val) === counter) { counter = counter + 1 }

      let aa = Object.keys(cloneLinkedGD)[i] + '/' + counter + ";";
      Sensorposition += aa;
    }
    Sensorposition += currEle + "/" + parseInt(val);

    const objToUpdate = {
      deviceDataModelId: "6df2d80f-be28-47ac-a509-8f0b6aa3bbb8",
      data: {
        Fullname: userWithOrder?.data?.Fullname?.value || obj?.fullName,
        Userid: userWithOrder?.data?.Userid?.value || obj?.userId,
        Sensorposition: Sensorposition,
        TimestampUP: moment().utc().format("YYYY-MM-DDTHH:mm:ss")
      },
      deviceDataId: userWithOrder?.deviceDataId,
      devicePropertySetId: device?.deviceDataById?.devicePropertySetId
    }

    if (!Object.keys(userWithOrder).length) {
      delete objToUpdate["deviceDataId"];
    }

    if (sensorsOrderData) {
      const matchedUserData = sensorsOrderData.find(ele => ele?.data?.Userid?.value === userInfo?.userId);
      if (matchedUserData?.data?.Sensorposition?.value) {
        matchedUserData.data.Sensorposition.value = Sensorposition;
        setUserWithOrder(matchedUserData);
        changeLocalDevicePosition();
      }
    }

    saveUserDeviceOrder(objToUpdate);
  }

  /* In case Guest Update, Guest data also should update on Local */
  useEffect(() => {
    const tempGuestSensors = [];
    if (guestMapData.guestsList?.length) {
      const guestSensorsMapped = guestMapData?.guestsList[0]?.data?.Macaddreess?.value;
      if (guestSensorsMapped?.length) {
        if (guestSensorsMapped?.length > 1) {
          tempGuestSensors.push(device?.friendlyNameData[0]);
        }
        for (let j = 0; j < device?.friendlyNameData?.length; j++) {
          if (guestSensorsMapped.indexOf(device?.friendlyNameData[j]?.macId) !== -1) {
            tempGuestSensors.push(device?.friendlyNameData[j]);
          }
        }
      }
      setGuestSensors(tempGuestSensors);

      if (autoFetchData && loggedUserRole === "Guest") {
        setAutoFetchData(0);
        getDeviceData();
      }
    }
  }, [guestMapData.guestsList, device?.friendlyNameData]);

  const getOwnerFilter = () => {
    try {
      let ownerFilter = {};
      if (loggedUserRole === "Patient") {
        ownerFilter = { users: [loggedUserId] };
      } else {
        if (userInfo.userId === "all" && loggedUserRole !== "Guest") {
          ownerFilter = {
            users: usersList.reduce((acc, val) => {
              if (val?.defaultRole?.supplier?.supplierId === userInfo?.supplierId) {
                return [...acc, val.userId];
              }
              return acc;
            }, []),
          };
        } else {
          ownerFilter = { users: userInfo.userId && userInfo.userId !== "all" ? [userInfo.userId] : [] };
        }
      }

      return ownerFilter;
    } catch(e) {
      console.log(e);
    }
  }

  const getParamsDetails = async() => {
    const payload = {
      deviceDataModelId: "d36a4373-fbdc-44a3-8c96-4bb920041e40-pa",
      devicePropertyCodes: ["Lteinterval", "FriendlyNamePa", "DevidParams", "NightMode", "NightModeStart", "NightModeEnd", "GainSelect", "BpfSelect", "HrLimitHi", "HrLimitLo", "Ltemode", "ForceWin"],
      ownerFilter: { users: [loggedUserId] }
    }

    try {
      payload.ownerFilter = getOwnerFilter();
      const endPoint = `${API_BASE_URL}/data/devicedata-advanced?sortBy=data.Timestamp.value&sortOrder=DESC`;
      const paramsData = await fetchRequest("POST", endPoint, payload);
      if(paramsData?.content?.length) {
        setParamsData(paramsData?.content);
      }

    } catch (err) {
      console.error("Error posting data:", err);
    }    
  }

  const getThresholdData = async() => {
    const payload = {
      deviceDataModelId: "d36a4373-fbdc-44a3-8c96-4bb920041e40-th",
      devicePropertyCodes: ["DevidThresholds", "AlertMinhr", "AlertMaxhr", "AlertMinrr", "AlertMaxrr", "AlertMinst", "AlertMaxst", "AlertMinmt", "AlertMaxmt"],
      ownerFilter: { users: [loggedUserId] }
    }

    try {
      payload.ownerFilter = getOwnerFilter();
      const endPoint = `${API_BASE_URL}/data/devicedata-advanced?sortBy=data.Timestamp.value&sortOrder=DESC`;
      const thresholdData = await fetchRequest("POST", endPoint, payload);
      console.log(thresholdData, 'threashold data');
      if(thresholdData?.content?.length) {
        setThresholdData(thresholdData?.content);
      }
    } catch (err) {
      console.error("Error posting data:", err);
    }
  }

  const fetchAndDownloadCSVData = async(forceDownload = false) => {
    if(userInfo?.fullName?.toLowerCase() === "Select All" || userInfo?.noSensorExist) {
      userInfo?.noSensorExist && setAlertMessage("No sensor found for the selected user");
      userInfo?.fullName?.toLowerCase() === "select all" && setAlertMessage("Please select a user");
      setToastStatus(true);
      return;
    }
    // Conditionally include Stress and Motion headers based on view mode
    const fdaViewHeaders = [
      "Device_Name", "Mac_ID", "Friendly_name", "Account Email", "HR", "RR", "ST", "MT", "Active", "FWvers", "HWvers", "WiFiRss", "Online", "Occupied",
      "First Updated", "Last updated", "TimeZone", "NightMode", "NightM Start", "NightM End", "Enable Notifications",
      "Threshold HR_H", "Threshold HR_L", "Threshold RR_H", "Threshold RR_L", "Threshold ST_H", "Threshold ST_L", "Threshold MT_H", "Threshold MT_L", "Wifi1", "Password1", "Wifi2", "Password2", "Wifi3", "Password3",
      "BPF", "GAIN", "LTE Mode", "LTE Interval", "HR_LIMIT_HI", "HR_LIMIT_LO", "Dev Mode", "Xcode", "Force FW"
    ];

    const defaultViewHeaders = [
      "Device_Name", "Mac_ID", "Friendly_name", "Account Email", "HR", "RR", "Active", "FWvers", "HWvers", "WiFiRss", "Online", "Occupied",
      "First Updated", "Last updated", "TimeZone", "NightMode", "NightM Start", "NightM End", "Wifi1", "Password1", "Wifi2", "Password2", "Wifi3", "Password3"
    ];
    
    let commonHeaders = viewMode === 'hrrr' ? defaultViewHeaders : fdaViewHeaders;
    const isManufacturer = ["TenantAdmin", "TenantUser"].includes(loggedUserRole);

    if(!isManufacturer) {
      const headersToRemove = new Set([
        "BPF", "GAIN", "LTE Mode", "LTE Interval", "HR_LIMIT_HI", "HR_LIMIT_LO",
        "Dev Mode", "Xcode", "Force FW", "HR", "RR", "ST", "MT",
        "Wifi1", "Password1", "Wifi2", "Password2", "Password3", "Wifi3"
      ]);
      commonHeaders = commonHeaders.filter(header => !headersToRemove.has(header));
    }
    
    const fileName = device.graphData?.[0]?.fullName?.replace(/ /g, "_") + "_" + moment().format("YYYY-MM-DD_HH-mm-ss") + ".csv";

    /* Identity Table data */
    const endPoint = `${API_BASE_URL}/data/devicedata-advanced/owner?pageNumber=0&pageSize=500&sortBy=maxValueProvidedOn&sortOrder=DESC`;
    const devHealthEndpoint = `${API_BASE_URL}/data/devicedata/aggregate-advanced`;
        
    const payload = {
      "deviceDataModelId": "d36a4373-fbdc-44a3-8c96-4bb920041e40-id",
      "devicePropertyCodes": [ "Devid", "Pw1", "Pw2", "Pw3", "Ssid1", "Ssid2", "Ssid3"],
      "dataTypes": [ "Text", "Text", "Text", "Text", "Text", "Text", "Text"],
      "devicePropertySetId": "fdab73ae-28df-45f5-92c5-b24460daf682",
      "ownerFilter": {"users": [userInfo?.userId]}
    };

    const rangeEndDateTime = moment().utc().format("YYYY-MM-DDTHH:mm:ss");
    const devHealthPayload = {
      "deviceDataModelId": "d36a4373-fbdc-44a3-8c96-4bb920041e40-dh",
      "aggregationPropertyOptions": [
        {"propertyCode": "TimestampD", "aggregationType": "First"},
        {"propertyCode": "TimestampD", "aggregationType": "Last"}
      ],
      "deviceCriteriaGroup": {
          "isOr": false,
          "groupOwner": null,
          "groupElements": [
            {"isOr": false, "groupOwner": null, "groupElements": [{"key": "TimestampD", "operator": "Between", "valueFrom": moment(rangeEndDateTime).subtract(1, "year").format("YYYY-MM-DDTHH:mm:ss"), "valueTo": rangeEndDateTime}]}
          ]
      },
      "groupingPropertyOptions": [{"propertyCode": "DevidDevhealth"}],
      "firstOrLastAggregationTypeSortBy": "TimestampD",
      "ownerFilter": {"users": [userInfo?.userId]}
    }

    if(userInfo?.friendlyName && userInfo?.friendlyName !== "all") {
      payload.deviceCriteria = [{"key": "Devid", "operator": "Equal", "value": userInfo?.friendlyName, "valueFrom": "", "valueTo": "", "isDateValue": false, "dateTimeRangeValue": null}];
    }
    
    const identityData = await fetchRequest("POST", endPoint, payload);
    const devHealthData = await fetchRequest("POST", devHealthEndpoint, devHealthPayload);
    console.log(identityData, 'identity data', devHealthData, 'dev health data');
    /* Identity Table data */

    const sensorsData = [];
    const allDevicesList = device?.graphData?.[0]?.linkedGraphData;
    /* console.log(device?.deviceList, 'device list'); */
    if(allDevicesList && Object.keys(allDevicesList)?.length) {
      Object.keys(allDevicesList).map((val, i) => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = currentPage * itemsPerPage;
        if(isConfigurationPage || (i >= startIndex && i < endIndex)) {
          const linkedGraphDataVal = allDevicesList[val];
          const deviceData = device.graphData?.[0]?.deviceIds.find(ele => ele?.Devid?.value === val);
          const devHealthItemData = devHealthData?.content?.find(ele => ele?.data?.DevidDevhealth?.value === val);
          const identityItemData = identityData?.content?.find(ele => ele?.Devid?.value === val);
          const deviceParamsData = paramsData.find(ele => ele?.data?.DevidParams?.value === val);
          const deviceThresholdData = thresholdData.find(ele => ele?.data?.DevidThresholds?.value === val);

          // Base data object with common fields
          const baseData = {
            First_Updated: moment(devHealthItemData?.data?.['First-TimestampD']?.value).format(CSV_DATE_FORMAT) || "--",
            Last_updated: moment(devHealthItemData?.data?.['Last-TimestampD']?.value).format(CSV_DATE_FORMAT),
            Threshold_HR_H: deviceThresholdData?.data?.AlertMaxhr?.value || "--",
            Threshold_HR_L: deviceThresholdData?.data?.AlertMinhr?.value || "--",
            Threshold_RR_H: deviceThresholdData?.data?.AlertMaxrr?.value || "--",
            Threshold_RR_L: deviceThresholdData?.data?.AlertMinrr?.value || "--",
            Occupied: linkedGraphDataVal?.data[0]?.data?.Occupied?.value,
            NightM_Start: deviceParamsData?.data?.NightModeStart?.value,
            Online: linkedGraphDataVal?.data[0]?.data?.Online?.value,
            NightM_End: deviceParamsData?.data?.NightModeEnd?.value,
            NightMode: deviceParamsData?.data?.NightMode?.value,
            Account_Email: device.graphData?.[0]?.emailAddress,
            HR: linkedGraphDataVal?.data[0]?.data?.Hr?.value,
            RR: linkedGraphDataVal?.data[0]?.data?.Rr?.value,
            Friendly_name: linkedGraphDataVal?.FriendlyName,
            Enable_Alerts: deviceData?.EnableAlerts?.value,
            Device_Name: userInfo.deviceName || "ATX2410",
            SMS: deviceData?.NotificationType?.value,
            TimeZone: linkedGraphDataVal?.timezone,
            WiFiRss: linkedGraphDataVal?.WifiRss,
            Active: linkedGraphDataVal?.active,
            FWvers: linkedGraphDataVal?.Fwvers,
            HWvers: linkedGraphDataVal?.Hwvers,
            Password1: identityItemData?.data?.Pw1?.value,
            Password2: identityItemData?.data?.Pw2?.value,
            Password3: identityItemData?.data?.Pw3?.value,
            Wifi1: identityItemData?.data?.Ssid1?.value,
            Wifi2: identityItemData?.data?.Ssid2?.value,
            Wifi3: identityItemData?.data?.Ssid3?.value,
            Mac_ID: val,
          };

          // Add manufacturer-specific data only for non-Patient and non-Guest users
          if (loggedUserRole !== "Patient" && loggedUserRole !== "Guest") {
            baseData.Force_FW = deviceParamsData?.data?.ForceWin?.value || "--";
            baseData.LTE_Interval = deviceParamsData?.data?.Lteinterval?.value;
            baseData.HR_LIMIT_LO = deviceParamsData?.data?.HrLimitLo?.value;
            baseData.HR_LIMIT_HI = deviceParamsData?.data?.HrLimitHi?.value;
            baseData.LTE_Mode = deviceParamsData?.data?.Ltemode?.value;
            baseData.GAIN = deviceParamsData?.data?.GainSelect?.value;
            baseData.BPF = deviceParamsData?.data?.BpfSelect?.value;
            baseData.Dev_Mode = "--";
            baseData.Xcode = "--";
          }

          // Add Stress and Motion data only if not in HR/RR view mode
          if (viewMode !== 'hrrr') {
            baseData.Threshold_MT_H = deviceThresholdData?.data?.AlertMaxmt?.value || "--";
            baseData.Threshold_MT_L = deviceThresholdData?.data?.AlertMinmt?.value || "--";
            baseData.Threshold_ST_H = deviceThresholdData?.data?.AlertMaxst?.value || "--";
            baseData.Threshold_ST_L = deviceThresholdData?.data?.AlertMinst?.value || "--";
            baseData.MT = linkedGraphDataVal?.data[0]?.data?.Motion?.value;
            baseData.ST = linkedGraphDataVal?.data[0]?.data?.Stress?.value;
          }

          sensorsData.push(baseData);
        }
      })
    }
    if(isConfigurationPage) {
      setDataCenterData({
        data: sensorsData,
        headers: commonHeaders
      });
    }

    if(forceDownload) {
      downloadBlobFile(arrayToCsvFormat(sensorsData, commonHeaders), fileName, "text/csv;charset=utf-8;");
    }
    // console.log(sensorsData, "Download CSV Data with file name", fileName, 'and headers', commonHeaders);
  }

  useEffect(() => {
    if(isConfigurationPage) {
      fetchAndDownloadCSVData();
    }
  }, [device?.graphData?.[0]?.linkedGraphData, viewMode])

  useEffect(() => {
    const newIsFdaMode = isFdaViewEnabled();
    
    if (newIsFdaMode !== isFdaMode) {
      setViewMode(newIsFdaMode ? 'hrrr' : 'default');
    }
  }, [location.search, isFdaMode])

  const handleApply = (filterData) => {
    setUserInfo(userInfo => {
      return { 
        ...userInfo, 
        noSensorExist: filterData?.noSensorExist,
        friendlyName: filterData?.friendlyName,
        deviceId: filterData?.selectedDevice,
        deviceName: filterData?.deviceName,
        supplierId: filterData?.company,
        facility: filterData?.facility,
        fullName: filterData?.fullName,
        userId: filterData?.user,
        fetchData: true,
      };
    });
  }

  useEffect(() => {
    if(userInfo.userId === "all") {
      setToastStatus(true);
      setAlertMessage("Select atleast one user to Fetch Data");
      setUserInfo(userInfo => {
        return { 
          ...userInfo,
          fetchData: false
        };
      });
      return;
    }
    if (userInfo.fetchData) {
      getDeviceData();
      setUserInfo(userInfo => {
        return { 
          ...userInfo, 
          fetchData: false
        };
      });
    }
  }, [userInfo.fetchData])

  return (
    <>
      {!["Guest"].includes(loggedUserRole) && <NewFiltersBox type="dashboard" onApply={handleApply} setUsersList={setUsersList} setCompanyList={setCompanyList} />}
      <GridContainer>
        {showSensors &&
        <>
          {((isConfigurationPage && dataCenterData?.data?.length > 0) || (!isConfigurationPage && showSensors)) && <div style={{margin: "0 20px 20px 0", textAlign: "right", width: "100%"}}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '15px' }}>
              <ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} showToggle={showToggle} />
              {device?.graphData?.[0]?.linkedGraphData && Object.keys(device?.graphData?.[0]?.linkedGraphData).length > 0 && (
                <Button disabled={!csvDownloadReady} onClick={() => fetchAndDownloadCSVData(true)} color="danger">
                  {isFdaViewEnabled() ? "DOWNLOAD DEVICE INFORMATION CSV" : "Download CSV"}
                </Button>
              )}
            </div>
          </div>}
          {!isConfigurationPage ? (
            <>
              <DevicesNew
                updateSensorControlTableRequest={updateSensorControlTableRequest}
                updateSensorControlRequest={updateSensorControlRequest}
                sensorControlRequest={sensorControlRequest}
                setShowSensorControl={setShowSensorControl}
                showSensorControl={showSensorControl}
                setUserMacAddress={setUserMacAddress}
                setDayLightSaving={setDayLightSaving}
                startGraphLoader={startGraphLoader}
                setUserTimeZone={setUserTimeZone}
                setAlertMessage={setAlertMessage}
                setFriendlyName={setFriendlyName}
                intervalChange={intervalChange}
                userData={device.sensorControl}
                loggedUserRole={loggedUserRole}
                userMacAddress={userMacAddress}
                setToastStatus={setToastStatus}
                onIndexUpdate={onIndexUpdate}
                userWithOrder={userWithOrder}
                userTimeZone={userTimeZone}
                itemsPerPage={itemsPerPage}
                friendlyName={friendlyName}
                currentPage={currentPage}
                setUserInfo={setUserInfo}
                lastFilter={lastFilter}
                setSensor={setSensor}
                setOffset={setOffset}
                setDrawer={setDrawer}
                userInfo={userInfo}
                viewMode={viewMode}
                setAbbr={setAbbr}
                sensor={sensor}
                device={device}
                offset={offset}
              />
              {device?.graphData?.[0]?.linkedGraphData && <Pagination
                totalItems={Object.keys(device?.graphData?.[0]?.linkedGraphData)?.length || 0}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
              />}
            </>
          ) : (
            <Configuration 
              dataCenterData={dataCenterData}
              showSensors={showSensors}
            />
          )}
        </>
        }
        {!device?.graphData?.length && !device.loadingData && loggedUserRole === "Guest" &&
          <p className='noContentFound'>No Sensor Found</p>
        }
      </GridContainer>
      <GraphDetail
        successNotificationRequest={successNotificationRequest}
        handleStartDateChange={handleStartDateChange}
        apiRequestCount={device.graphReqApiCount}
        apiResponseCount={device.graphApiCount}
        setDayLightSaving={setDayLightSaving}
        selectedStartDate={selectedStartDate}
        fetchButtonTapped={fetchButtonTapped}
        svittalDrawerRef={svittalDrawerRef}
        setDisablePdfBtn={setDisablePdfBtn}
        onIntervalChange={onIntervalChange}
        setUserTimeZone={setUserTimeZone}
        setFriendlyName={setFriendlyName}
        loggedUserRole={loggedUserRole}
        resetGraphData={resetGraphData}
        dayLightSaving={dayLightSaving}
        disablePdfBtn={disablePdfBtn}
        userTimeZone={userTimeZone}
        friendlyName={friendlyName}
        setDevDataId={setDevDataId}
        setUserInfo={setUserInfo}
        exportPdf={exportPdf}
        startTime={startTime}
        exportCSV={exportCSV}
        drawerRef={drawerRef}
        setOffset={setOffset}
        setDrawer={setDrawer}
        userInfo={userInfo}
        viewMode={viewMode}
        setAbbr={setAbbr}
        endTime={endTime}
        device={device}
        drawer={drawer}
        offset={offset}
        sensor={sensor}
        abbr={abbr}
      />
      <Snackbar
        closeNotification={() => setToastStatus(false)}
        message={alertMessage}
        open={toastStatus}
        icon={AddAlert}
        color="danger"
        place="tr"
        close
      />
      <Loader
        status={device.loading || (drawer ? device.loadingGraph : false) || device.loadingCsv || device.loadingData}
        crossIcon={drawer ? true : false}
        onDrawerClose={onDrawerClose}
      />
    </>
  );
}
